/**
 * Dry-run / "test flow" path: walks the graph from roots using live condition
 * evaluation where possible, otherwise follows the true branch for decisions.
 */

import type { FlowEdge, FlowNode } from '../types/flow-types';
import { sceneSystemV2FlowIntegration } from '../integration/SceneSystemV2FlowIntegration';
import { getEffectiveNodeDisplayLabel } from '../PropertyEditors/helpers/LabelGenerator';
import { analyzeFlowGraph, selectSwitchEdges } from './flowGraphSemantics';

const NOTE = 'note';

const CONDITION_LIKE = new Set([
	'condition',
	'timeCondition',
	'manualCondition',
	'weatherCondition',
	'sunriseSunsetCondition',
	'energyCondition',
	'mathCondition',
	'sceneCondition',
	'alarmCondition',
	'scheduleCondition',
	'variableCondition',
	'hysteresisCondition',
	'presenceCondition',
	'virtualComponentEvent',
	'virtualComponentCondition',
]);

export interface FlowSimulationResult {
	nodeIds: string[];
	edgeIds: string[];
	messages: string[];
	steps: FlowSimulationStep[];
	values: FlowSimulationValue[];
}

export interface FlowSimulationValue {
	name: string;
	value: unknown;
	producerNodeId: string;
	consumerNodeIds: string[];
}

export interface FlowSimulationStep {
	nodeId: string;
	label: string;
	kind: 'condition' | 'branch' | 'action' | 'control';
	status: 'matched' | 'not-matched' | 'executed' | 'assumed';
	detail: string;
	elapsedMs: number;
	observedValue?: unknown;
}

interface ConditionEvaluation {
	value: boolean;
	assumed: boolean;
	detail: string;
}

export interface FlowSimulationOptions {
	/** Deterministic condition outcomes used instead of live evaluation. */
	conditionResults?: Record<string, boolean>;
	/** Test values keyed by switch node id or its sourceRef. */
	switchValues?: Record<string, unknown>;
	/** Begin at one or more selected nodes instead of graph roots. */
	entryNodeIds?: string[];
}

function filterSemantic(nodes: FlowNode[], edges: FlowEdge[]): { nodes: FlowNode[]; edges: FlowEdge[] } {
	const semantic = nodes.filter((n) => n.type !== NOTE);
	const ids = new Set(semantic.map((n) => n.id));
	return {
		nodes: semantic,
		edges: edges.filter((e) => ids.has(String(e.source)) && ids.has(String(e.target))),
	};
}

function buildAdj(nodes: FlowNode[], edges: FlowEdge[]) {
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));
	const out = new Map<string, FlowEdge[]>();
	const indeg = new Map<string, number>();
	for (const n of nodes) {
		out.set(n.id, []);
		indeg.set(n.id, 0);
	}
	for (const e of edges) {
		const arr = out.get(String(e.source));
		if (arr) arr.push(e);
		const t = String(e.target);
		indeg.set(t, (indeg.get(t) ?? 0) + 1);
	}
	return { nodeMap, out, indeg };
}

function aggregateGate(gateType: string | undefined, vals: boolean[]): boolean {
	const g = (gateType || 'or').toLowerCase();
	if (g === 'and') return vals.length ? vals.every(Boolean) : true;
	if (g === 'nor') return !vals.some(Boolean);
	return vals.some(Boolean);
}

async function evalConditionNode(
	node: FlowNode,
	override: boolean | undefined,
): Promise<ConditionEvaluation> {
	if (typeof override === 'boolean') {
		return {
			value: override,
			assumed: false,
			detail: override
				? 'The supplied test value matched.'
				: 'The supplied test value did not match.',
		};
	}
	const cap = node.data?.sceneCapability as Record<string, unknown> | undefined;
	const deviceId = (cap?.deviceId as string) || (node.data?.device as { id?: string })?.id;
	const capabilityName = (cap?.capabilityName || cap?.name) as string | undefined;
	const channel = Number(
		(cap?.channel as number | string | undefined) ??
			(node.data?.device as unknown as { channel?: number | string })?.channel ??
			0,
	);

	if (deviceId && capabilityName) {
		await sceneSystemV2FlowIntegration.initialize();
		const params = (cap?.parameters as Record<string, unknown>) || {};
		const res = await sceneSystemV2FlowIntegration.testCapability(
			deviceId,
			'condition',
			channel,
			capabilityName,
			params as Record<string, unknown>,
		);
		const boolResult = (res as { result?: { result?: boolean } })?.result?.result;
		if (typeof boolResult === 'boolean') {
			return {
				value: boolResult,
				assumed: false,
				detail: boolResult ? 'Live device data matched.' : 'Live device data did not match.',
			};
		}
	}
	return {
		value: true,
		assumed: true,
		detail: 'No live evaluator is available yet, so preview follows the matching branch.',
	};
}

function pickDecisionEdges(node: FlowNode, edgesOut: FlowEdge[], condTrue: boolean): FlowEdge[] {
	const hasTf = edgesOut.some((e) => e.sourceHandle === 'true' || e.sourceHandle === 'false');
	if (!hasTf) return edgesOut;
	if (node.type === 'logicGate') {
		return edgesOut.filter((e) =>
			condTrue ? e.sourceHandle === 'true' : e.sourceHandle === 'false',
		);
	}
	if (CONDITION_LIKE.has(node.type || '') || node.type === 'condition') {
		return edgesOut.filter((e) =>
			condTrue ? e.sourceHandle === 'true' : e.sourceHandle === 'false',
		);
	}
	return edgesOut;
}

/**
 * Walk from roots following one branch per decision. Records visited nodes/edges.
 */
export async function simulateFlowGraph(
	nodes: FlowNode[],
	edges: FlowEdge[],
	options: FlowSimulationOptions = {},
): Promise<FlowSimulationResult> {
	const messages: string[] = [];
	const steps: FlowSimulationStep[] = [];
	const { nodes: sn, edges: se } = filterSemantic(nodes, edges);
	if (!sn.length) {
		return {
			nodeIds: [],
			edgeIds: [],
			messages: ['No nodes to simulate'],
			steps,
			values: [],
		};
	}

	const { nodeMap, out, indeg } = buildAdj(sn, se);
	const analysis = analyzeFlowGraph(sn, se);
	const requestedEntries = (options.entryNodeIds ?? [])
		.map((id) => nodeMap.get(id))
		.filter((node): node is FlowNode => Boolean(node));
	const roots = requestedEntries.length
		? requestedEntries
		: sn.filter((n) => (indeg.get(n.id) ?? 0) === 0);
	if (!roots.length) {
		messages.push('No root nodes (everything has an incoming edge). Trying all nodes as entry.');
		roots.push(...sn);
	}

	const activeNodes = new Set<string>();
	const activeEdges = new Set<string>();
	const visiting = new Set<string>();
	const completed = new Set<string>();
	const reportedCycles = new Set<string>();
	const reportedJoins = new Set<string>();

	const cache = new Map<string, boolean>();
	const evaluations = new Map<string, ConditionEvaluation>();
	const computing = new Set<string>();

	async function evalTruth(nodeId: string): Promise<boolean> {
		if (cache.has(nodeId)) return cache.get(nodeId)!;
		if (computing.has(nodeId)) return true;
		computing.add(nodeId);

		const node = nodeMap.get(nodeId);
		if (!node) {
			cache.set(nodeId, true);
			computing.delete(nodeId);
			return true;
		}

		if (node.type === 'logicGate') {
			const inE = se.filter((e) => String(e.target) === nodeId);
			const vals = await Promise.all(inE.map((e) => evalTruth(String(e.source))));
			const v = aggregateGate(node.data?.gateType as string, vals);
			cache.set(nodeId, v);
			computing.delete(nodeId);
			return v;
		}

		if (CONDITION_LIKE.has(node.type || '') || node.type === 'condition') {
			const evaluation = await evalConditionNode(node, options.conditionResults?.[nodeId]);
			evaluations.set(nodeId, evaluation);
			cache.set(nodeId, evaluation.value);
			computing.delete(nodeId);
			return evaluation.value;
		}

		cache.set(nodeId, true);
		computing.delete(nodeId);
		return true;
	}

	async function dfs(nodeId: string, fromEdgeId: string | null): Promise<void> {
		if (fromEdgeId) activeEdges.add(fromEdgeId);
		if (visiting.has(nodeId)) {
			if (!reportedCycles.has(nodeId)) {
				reportedCycles.add(nodeId);
				messages.push('Cycle stopped during preview. Cycles cannot be saved in a Flow.');
			}
			return;
		}
		if (completed.has(nodeId)) {
			if (analysis.joinNodeIds.includes(nodeId) && !reportedJoins.has(nodeId)) {
				reportedJoins.add(nodeId);
				messages.push(
					`"${nodeMap.get(nodeId)?.data?.label || nodeId}" is a merge point and runs once per preview.`,
				);
			}
			return;
		}
		visiting.add(nodeId);
		activeNodes.add(nodeId);

		const node = nodeMap.get(nodeId);
		if (!node) {
			visiting.delete(nodeId);
			return;
		}

		const edgesOut = [...(out.get(nodeId) ?? [])].sort((a, b) =>
			String(a.sourceHandle || '').localeCompare(String(b.sourceHandle || '')),
		);

		let nextEdges = edgesOut;
		const stepStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
		const elapsed = () =>
			Math.max(
				0,
				Math.round(
					(typeof performance !== 'undefined' ? performance.now() : Date.now()) -
						stepStartedAt,
				),
			);

		if (node.type === 'switchNode' || node.type === 'variableSwitch') {
			const sourceRef =
				node.type === 'variableSwitch'
					? `var:${String(node.data.variableName || '')}`
					: String(node.data.sourceRef ?? '');
			const hasNodeValue = Object.prototype.hasOwnProperty.call(
				options.switchValues ?? {},
				nodeId,
			);
			const hasRefValue =
				!!sourceRef &&
				Object.prototype.hasOwnProperty.call(options.switchValues ?? {}, sourceRef);
			const testValue = hasNodeValue
				? options.switchValues?.[nodeId]
				: options.switchValues?.[sourceRef];
			const selection = selectSwitchEdges(node, edgesOut, testValue, hasNodeValue || hasRefValue);
			nextEdges = selection.edges;
			steps.push({
				nodeId,
				label: getEffectiveNodeDisplayLabel(node),
				kind: 'branch',
				status: selection.assumed ? 'assumed' : 'matched',
				elapsedMs: elapsed(),
				observedValue: testValue,
				detail: selection.matchedCaseId
					? `Test value ${String(testValue)} matched case ${selection.matchedCaseId}.`
					: selection.assumed
						? 'No test value was supplied, so preview followed the default or first case.'
						: `Test value ${String(testValue)} matched no case, so preview followed the default.`,
			});
		} else if (
			node.type === 'logicGate' ||
			CONDITION_LIKE.has(node.type || '') ||
			node.type === 'condition'
		) {
			const outcome = await evalTruth(nodeId);
			const evaluation = evaluations.get(nodeId);
			nextEdges = pickDecisionEdges(node, edgesOut, outcome);
			steps.push({
				nodeId,
				label: getEffectiveNodeDisplayLabel(node),
				kind: node.type === 'logicGate' ? 'control' : 'condition',
				status: evaluation?.assumed ? 'assumed' : outcome ? 'matched' : 'not-matched',
				elapsedMs: elapsed(),
				observedValue: outcome,
				detail:
					evaluation?.detail ||
					(outcome
						? 'Incoming results satisfy this gate.'
						: 'Incoming results do not satisfy this gate.'),
			});
			if (!nextEdges.length && edgesOut.length) {
				messages.push(`"${node.data?.displayLabel || nodeId}": no edge for branch — stopping.`);
			}
		} else {
			steps.push({
				nodeId,
				label: getEffectiveNodeDisplayLabel(node),
				kind: node.type === 'timer' ? 'control' : 'action',
				status: 'executed',
				elapsedMs: elapsed(),
				detail:
					node.type === 'timer'
						? 'The preview includes this timing step without waiting in real time.'
						: 'This action is on the simulated path; no real action was sent.',
			});
		}

		for (const e of nextEdges) {
			await dfs(String(e.target), String(e.id));
		}

		visiting.delete(nodeId);
		completed.add(nodeId);
	}

	await Promise.all(roots.map((r) => dfs(r.id, null)));

	const values = sn
		.filter((node) => node.type === 'setVariable')
		.map((node): FlowSimulationValue => {
			const variable = node.data.variable as { name?: string; value?: unknown } | undefined;
			const name = String(variable?.name || node.data.variableName || '').trim();
			const value = variable?.value ?? node.data.value;
			return {
				name: name || 'Unnamed variable',
				value,
				producerNodeId: node.id,
				consumerNodeIds: sn
					.filter(
						(candidate) =>
							candidate.id !== node.id &&
							(String(candidate.data.sourceRef || '') === `var:${name}` ||
								String(candidate.data.variableName || '') === name),
					)
					.map((candidate) => candidate.id),
			};
		});

	return {
		nodeIds: [...activeNodes],
		edgeIds: [...activeEdges],
		messages,
		steps,
		values,
	};
}
